import React, { useState } from "react";
import { Button } from '@/components/ui/Button';
import { X } from 'lucide-react';
import { useUnifiedAuth } from '@/lib/auth/use-unified-auth';

// Types for product, cart, and cart item
export type Product = {
  id: string;
  type: string;
  label: string;
  description: string;
  price: number;
};
export type CartItem = {
  id: string;
  product_id: string;
  quantity: number;
};
export type Cart = {
  items: CartItem[];
};

// Props for the cart component
interface SingleExperienceCartProps {
  experience: { title: string };
  products: Product[];
  cart: Cart;
  onAddToCart: (args: { productId: string; quantity: number; details?: object }) => void;
  onUpdateCartItem: (itemId: string, updates: object) => void;
  onRemoveCartItem: (itemId: string) => void;
  onCheckout: () => void;
  loading: boolean;
  onRequireAuth: () => void;
  onClose?: () => void;
}

export function SingleExperienceCart({
  experience,
  products = [],
  cart,
  onAddToCart,
  onUpdateCartItem,
  onRemoveCartItem,
  onCheckout,
  loading,
  onRequireAuth,
  onClose
}: SingleExperienceCartProps) {
  const { user } = useUnifiedAuth();
  const [quantities, setQuantities] = useState<{ [productId: string]: number }>({});

  // Group products by type
  const tickets = products.filter((p: Product) => p.type === 'ticket');
  const lodging = products.filter((p: Product) => p.type === 'lodging');
  const addOns = products.filter((p: Product) => p.type === 'addon');

  // Handle quantity change
  const handleQuantityChange = (productId: string, delta: number) => {
    setQuantities(q => ({
      ...q,
      [productId]: Math.max(0, (q[productId] || 0) + delta)
    }));
  };

  // Add to cart handler
  const handleAddToCart = (product: Product) => {
    if (!user) {
      onRequireAuth && onRequireAuth();
      return;
    }
    onAddToCart({
      productId: product.id,
      quantity: quantities[product.id] || 1,
      details: {}
    });
  };

  // Remove from cart handler
  const handleRemove = (itemId: string) => {
    onRemoveCartItem(itemId);
  };

  // Calculate total
  const getTotal = () => {
    let total = 0;
    if (cart && cart.items) {
      cart.items.forEach(item => {
        const product = products.find(p => p.id === item.product_id);
        if (product) {
          total += (product.price || 0) * (item.quantity || 1);
        }
      });
    }
    return total;
  };

  // Modal container styles
  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 flex justify-center items-end sm:items-center sm:p-8">
      <div className="relative w-full max-w-md bg-earth-900 border border-earth-700 shadow-2xl rounded-t-2xl sm:rounded-2xl p-6 sm:p-8 text-sand-100">
        {/* Close button for mobile UX */}
        {onClose && (
          <button
            className="absolute top-4 right-4 text-sand-400 hover:text-sand-200"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="h-6 w-6" />
          </button>
        )}
        <h2 className="text-xl font-bold mb-4 text-sand-100">{experience.title}</h2>
        {/* Tickets Section */}
        {tickets.length > 0 && (
          <div className="mb-4">
            <h3 className="font-semibold text-sand-200 mb-2">Tickets</h3>
            <div className="space-y-2">
              {tickets.map(ticket => (
                <div key={ticket.id} className="flex items-center justify-between py-2 border-b border-earth-700 last:border-b-0">
                  <div>
                    <div className="font-medium text-sand-100">{ticket.label}</div>
                    <div className="text-xs text-sand-300">{ticket.description}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" className="bg-earth-800 text-sand-200" onClick={() => handleQuantityChange(ticket.id, -1)} disabled={(quantities[ticket.id] || 0) === 0}>-</Button>
                    <span className="w-5 text-center text-sand-100 font-semibold">{quantities[ticket.id] || 0}</span>
                    <Button size="sm" className="bg-earth-800 text-sand-200" onClick={() => handleQuantityChange(ticket.id, 1)}>+</Button>
                    <Button size="sm" variant="primary" className="bg-sand-200 text-earth-900 font-bold" onClick={() => handleAddToCart(ticket)} disabled={loading || (quantities[ticket.id] || 0) === 0}>ADD</Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Lodging Section */}
        {lodging.length > 0 && (
          <div className="mb-4">
            <h3 className="font-semibold text-sand-200 mb-2">Lodging</h3>
            <div className="space-y-2">
              {lodging.map(lodge => (
                <div key={lodge.id} className="flex items-center justify-between py-2 border-b border-earth-700 last:border-b-0">
                  <div>
                    <div className="font-medium text-sand-100">{lodge.label}</div>
                    <div className="text-xs text-sand-300">{lodge.description}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" className="bg-earth-800 text-sand-200" onClick={() => handleQuantityChange(lodge.id, -1)} disabled={(quantities[lodge.id] || 0) === 0}>-</Button>
                    <span className="w-5 text-center text-sand-100 font-semibold">{quantities[lodge.id] || 0}</span>
                    <Button size="sm" className="bg-earth-800 text-sand-200" onClick={() => handleQuantityChange(lodge.id, 1)}>+</Button>
                    <Button size="sm" variant="primary" className="bg-sand-200 text-earth-900 font-bold" onClick={() => handleAddToCart(lodge)} disabled={loading || (quantities[lodge.id] || 0) === 0}>ADD</Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Add-ons Section */}
        {addOns.length > 0 && (
          <div className="mb-4">
            <h3 className="font-semibold text-sand-200 mb-2">Add-ons</h3>
            <div className="space-y-2">
              {addOns.map(addon => (
                <div key={addon.id} className="flex items-center justify-between py-2 border-b border-earth-700 last:border-b-0">
                  <div>
                    <div className="font-medium text-sand-100">{addon.label}</div>
                    <div className="text-xs text-sand-300">{addon.description}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" className="bg-earth-800 text-sand-200" onClick={() => handleQuantityChange(addon.id, -1)} disabled={(quantities[addon.id] || 0) === 0}>-</Button>
                    <span className="w-5 text-center text-sand-100 font-semibold">{quantities[addon.id] || 0}</span>
                    <Button size="sm" className="bg-earth-800 text-sand-200" onClick={() => handleQuantityChange(addon.id, 1)}>+</Button>
                    <Button size="sm" variant="primary" className="bg-sand-200 text-earth-900 font-bold" onClick={() => handleAddToCart(addon)} disabled={loading || (quantities[addon.id] || 0) === 0}>ADD</Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Cart Review */}
        {cart && cart.items && cart.items.length > 0 && (
          <div className="mt-6 border-t border-earth-700 pt-4">
            <h3 className="font-semibold text-sand-200 mb-2">Your Booking</h3>
            <div className="space-y-2">
              {cart.items.map(item => {
                const product = products.find(p => p.id === item.product_id);
                if (!product) return null;
                return (
                  <div key={item.id} className="flex justify-between items-center mb-2">
                    <div>
                      <span className="font-medium text-sand-100">{product.label}</span>
                      <span className="ml-2 text-xs text-sand-300">x{item.quantity}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sand-100">€{(product.price * item.quantity).toFixed(2)}</span>
                      <Button size="sm" variant="secondary" className="text-sand-400 hover:text-sand-200" onClick={() => handleRemove(item.id)}>Remove</Button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between font-bold mt-2 text-sand-100">
              <span>Total</span>
              <span>€{getTotal().toFixed(2)}</span>
            </div>
            <Button
              className="w-full bg-sand-200 text-earth-900 py-3 rounded-lg mt-4 text-lg font-semibold hover:bg-sand-100"
              onClick={() => {
                if (!user) {
                  onRequireAuth && onRequireAuth();
                  return;
                }
                onCheckout();
              }}
              disabled={loading}
            >
              {loading ? "Processing..." : "Checkout"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
} 